import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useBlocker, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import LogRocket from 'logrocket';
import { nanoid } from 'nanoid';
import { useStatsigClient } from '@statsig/react-bindings';

import {
  createInstantSurvey,
  DEFAULT_GROUP_NAME,
  MAINTENANCE_MESSAGE,
  readInstantSurvey,
  readInstantSurveyType,
  RESEARCH_OBJECTIVES_MAX_LENGTH,
  SURVEY_GROUP_VALIDATION_STATUS,
  SURVEY_TYPES,
  updateInstantSurvey,
  validateInstantSurveyGroup,
} from '@api/instant_survey';
import {
  createAtlasSurvey,
  pausedEditableSurveyStatuses,
  pausedReadOnlySurveyStatuses,
  readSurvey,
  readSurveyType,
  surveyStatus,
  updateSurveyTitle,
} from '@api/surveys';
import { readClientSubscription } from '@api/client';

import { GATES } from '@constants';
import { updateUserCreditsRemaining, updateUserSubscriptionDetails } from '@redux/slices/user';
import {
  Announcement,
  Button,
  CheckSquareIcon,
  Error,
  ExclamationTriangleIcon,
  Layout,
  LoaderSkeleton,
  LockIcon,
  MaintenanceView,
  ModalV2 as Modal,
  ShieldTipIcon,
  toast,
  useAutoOpenAfterDelay,
} from '@utilities';

import Confirmation from './components/Confirmation';
import ModalUnsavedChanges from './components/ModalUnsavedChanges';
import Page from '../../layouts/Page';
import Panelists from './components/Panelists';
import Questionnaire from './components/Questionnaire';
import Review from './components/Review';
import SurveyNetworkError from '../../components/SurveyNetworkError';

import { payWithCompletes, payWithCredits } from '../Checkout/utilities/constants';

import {
  AUTO_SAVE_DEBOUNCE_MS,
  MAX_AUTO_SAVE_RETRIES,
  SURVEY_STEPS,
  SURVEY_TEMPORARY_TITLE,
} from './utilities/constants';
import { SurveyContext } from './utilities/context';
import {
  buildPromptAnswersKey,
  ensureQuestionClientIds,
  generateTemporarySurveyTitle,
  getCurrentQuestionnairePrice,
  getModalConfig,
  getQuestionDisplayIndexById,
  getQuestionsCost,
  getQuotaLimit,
  isQuestionLogicComplete,
  mergeGroups,
  mergeQuestionLogic,
  mergeQuestions,
  mergeServerIdsFromStaleResponse,
} from './utilities/helpers';

import { default as NumeratorLogo } from '../../components/Header/assets/data-wave-logo.svg?react';

import styles from './_index.module.scss';

const CreateInstantSurvey = () => {
  const [activeStep, setActiveStep] = useState(SURVEY_STEPS.PANELISTS);
  const [autoSaveError, setAutoSaveError] = useState(null);
  const [error, setError] = useState();
  const [hasLaunched, setHasLaunched] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [insightsDocument, setInsightsDocument] = useState();
  const [insightsDocumentType, setInsightsDocumentType] = useState();
  const [instantSurveyType, setInstantSurveyType] = useState();
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [isLoadingSurvey, setIsLoadingSurvey] = useState(true);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const [isSaveAsDraftOpen, setIsSaveAsDraftOpen] = useState(false);
  const [isSavingAsDraft, setIsSavingAsDraft] = useState(false);
  const [jobId, setJobId] = useState('');
  const [lastAutoSavedAt, setLastAutoSavedAt] = useState(null);
  const [showAIGenerationModal, setShowAIGenerationModal] = useState(false);
  const [showSurveyTemplateModal, setShowSurveyTemplateModal] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [surveyGoals, setSurveyGoals] = useState('');

  const {
    clientInfo,
    panel = {},
    permissions = {},
  } = useSelector((state) => state?.user?.data) || {};

  const canAdvancedEditSurvey = permissions.canAdvancedEditSurvey;
  const isReadOnly =
    pausedReadOnlySurveyStatuses.includes(insightsDocument?.status) && !canAdvancedEditSurvey;
  const isSubscribed = !!clientInfo?.subscriptions?.length;
  const [launchMethod, setLaunchMethodState] = useState(
    isSubscribed ? payWithCompletes.key : payWithCredits.key
  );

  const { client, isLoading: isStatsigLoading } = useStatsigClient();
  const isAutoSaveGateEnabled = client.checkGate(GATES.INSTANT_SURVEY_AUTO_SAVE);
  const isInstantSurveyMaintenance = client.checkGate(GATES.INSTANT_SURVEY_MAINTENANCE);
  const isRealtimeSampleValidationEnabled = client.checkGate(GATES.REAL_TIME_SAMPLE_VALIDATION);
  const isInternalNavigationRef = useRef(false);
  const useCalypsoProvider = client.checkGate(GATES.USE_CALYPSO_AS_SURVEY_PROVIDER);

  const { instant_survey_type_id: instantSurveyTypeId } = useParams();

  const defaultResponseCount = getQuotaLimit(instantSurveyTypeId);

  const [survey, setSurvey] = useState({
    country: panel?.abbrev?.toLowerCase(),
    groups: [
      {
        clientId: nanoid(),
        genRepSampleType: null,
        id: null,
        name: DEFAULT_GROUP_NAME,
        promptAnswers: [],
        quotaLimit: defaultResponseCount,
      },
    ],
    id: null,
    instantSurveyType: instantSurveyTypeId,
    questionLogic: {},
    questions: [],
    researchObjectives: '',
    surveyTemplateId: null,
    title: '',
  });
  const autoSaveTimeoutRef = useRef(null);
  const autoSaveAbortControllerRef = useRef(null);
  const autoSaveRetryCountRef = useRef(0);
  const autoSaveRetryTimeoutRef = useRef(null);
  const lastPersistedTitleRef = useRef('');
  const persistSurveyDraftPromiseRef = useRef(null);
  const isComponentMountedRef = useRef(false);
  const insightsDocumentRef = useRef();
  const hasUnsavedChangesRef = useRef(false);
  const surveyRef = useRef(null);
  const pendingInternalNavigationParamsRef = useRef(null);
  const groupValidationAbortControllersRef = useRef(new Map());
  const lastValidationFingerprintByClientIdRef = useRef(new Map());
  const pendingGroupValidationClientIdsRef = useRef(new Set());
  const [validatingGroupClientIds, setValidatingGroupClientIds] = useState([]);
  surveyRef.current = survey;

  // versioning so we don’t clear dirty for stale saves
  const surveyVersionRef = useRef(0);

  const assignClientIds = useCallback((nextGroups = [], prevGroups = []) => {
    const clientIdByServerId = new Map(
      prevGroups
        .filter(({ clientId, id }) => id !== null && id !== undefined && clientId)
        .map(({ clientId, id }) => [id, clientId])
    );

    const normalizeValueSignature = (value) => {
      if (value && typeof value === 'object') {
        if (value.id !== undefined && value.id !== null) {
          return `id:${value.id}`;
        }
        const sortedKeys = Object.keys(value).sort();
        return `obj:${sortedKeys.map((key) => `${key}:${JSON.stringify(value[key])}`).join('|')}`;
      }
      return `${typeof value}:${String(value)}`;
    };

    const createGroupSignature = (group) => {
      if (!group) return '';
      const promptAnswersSignature = (group.promptAnswers || []).filter(Boolean).map((answer) => ({
        field: answer.field ?? '',
        operator: answer.operator ?? '',
        promptId: answer.promptId ?? '',
        values: (answer.values || []).map((value) => normalizeValueSignature(value)),
      }));

      return JSON.stringify({
        genRepSampleType: group.genRepSampleType ?? '',
        name: group.name?.trim().toLowerCase() ?? '',
        promptAnswers: promptAnswersSignature,
        quotaLimit: group.quotaLimit ?? null,
      });
    };

    const unsavedPrevGroups = prevGroups
      .filter((group) => group?.id === null || group?.id === undefined)
      .map((group) => ({
        clientId: group?.clientId,
        signature: createGroupSignature(group),
      }));

    const unsavedGroupsBySignature = new Map();
    unsavedPrevGroups.forEach((group) => {
      if (!group.signature) return;
      if (!unsavedGroupsBySignature.has(group.signature)) {
        unsavedGroupsBySignature.set(group.signature, []);
      }
      unsavedGroupsBySignature.get(group.signature).push(group);
    });

    const fallbackUnsavedQueue = [...unsavedPrevGroups];

    const getClientIdForNewGroup = (group) => {
      const signature = createGroupSignature(group);
      if (signature && unsavedGroupsBySignature.has(signature)) {
        const candidates = unsavedGroupsBySignature.get(signature);
        while (candidates.length) {
          const candidate = candidates.shift();
          const queueIndex = fallbackUnsavedQueue.indexOf(candidate);
          if (queueIndex !== -1) fallbackUnsavedQueue.splice(queueIndex, 1);
          if (candidate) {
            return candidate.clientId ?? nanoid();
          }
        }
        unsavedGroupsBySignature.delete(signature);
      }

      while (fallbackUnsavedQueue.length) {
        const candidate = fallbackUnsavedQueue.shift();
        if (candidate) return candidate.clientId ?? nanoid();
      }

      return null;
    };

    return nextGroups.map((group, index) => {
      const persistedClientId = clientIdByServerId.get(group.id) ?? group.clientId ?? null;

      if (persistedClientId) {
        return { ...group, clientId: persistedClientId };
      }

      let clientId = null;

      if (group.id !== null && group.id !== undefined) {
        clientId = getClientIdForNewGroup(group);
      }

      if (!clientId) {
        clientId = prevGroups[index]?.clientId ?? nanoid();
      }

      return { ...group, clientId };
    });
  }, []);

  const activeGroups = survey.groups.filter(({ _delete }) => !_delete);
  const activeQuestions = survey.questions.filter(({ _delete }) => !_delete);

  const isMonadicConceptTest = instantSurveyType?.id === SURVEY_TYPES.MONADIC;
  const isTURF = instantSurveyType?.id === SURVEY_TYPES.TURF;
  const isNewItemSurvey = instantSurveyType?.id === SURVEY_TYPES.NEW_ITEM;
  const isVideoSurvey = instantSurveyType?.id === SURVEY_TYPES.VIDEO;
  const isSequentialMonadic = instantSurveyType?.id === SURVEY_TYPES.SEQUENTIAL_MONADIC;

  const currentQuestionCount = getQuestionsCost(activeQuestions);
  const currentQuestionnairePrice = useMemo(
    () =>
      getCurrentQuestionnairePrice({
        questionnairePricing: insightsDocumentType?.questionnairePricing,
        survey,
      }),
    [survey, insightsDocumentType?.questionnairePricing]
  );

  const questionDisplayIndexById = useMemo(
    () => getQuestionDisplayIndexById(survey.questions),
    [survey.questions]
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const insightsId = searchParams.get('id');

  const infoModalSurveyType = useMemo(() => {
    if (isMonadicConceptTest) {
      return 'monadic';
    } else if (isTURF) {
      return 'turf';
    } else if (isNewItemSurvey) {
      return 'new-item';
    } else if (isSequentialMonadic) {
      return 'sequential-monadic';
    }
  }, [isMonadicConceptTest, isTURF, isNewItemSurvey, isSequentialMonadic]);

  const infoModalConfig =
    !insightsId && infoModalSurveyType ? getModalConfig({ surveyType: infoModalSurveyType }) : null;

  const { close: closeInfoModal, isOpen: isInfoModalOpen } = useAutoOpenAfterDelay(
    500,
    infoModalSurveyType
  );

  const shouldBlock = useCallback(() => {
    if (!isAutoSaveGateEnabled) return false;
    if (isInternalNavigationRef.current) return false;
    return !(hasLaunched || isInstantSurveyMaintenance || isSavingAsDraft) && hasUnsavedChanges;
  }, [
    hasLaunched,
    hasUnsavedChanges,
    isAutoSaveGateEnabled,
    isInstantSurveyMaintenance,
    isSavingAsDraft,
  ]);
  const blocker = useBlocker(shouldBlock);
  const canAutoSave = useMemo(() => {
    return (
      isAutoSaveGateEnabled &&
      !isInstantSurveyMaintenance &&
      !isLoadingSurvey &&
      !isReadOnly &&
      !isStatsigLoading &&
      !hasLaunched
    );
  }, [
    isAutoSaveGateEnabled,
    isInstantSurveyMaintenance,
    isLoadingSurvey,
    isStatsigLoading,
    hasLaunched,
  ]);
  const canAutoSaveRef = useRef(canAutoSave);
  useEffect(() => {
    canAutoSaveRef.current = canAutoSave;
  }, [canAutoSave]);

  const syncInsightsDocument = useCallback((document) => {
    insightsDocumentRef.current = document;
    if (!isComponentMountedRef.current) return;
    setInsightsDocument(document);
  }, []);

  const abortAutoSaveRequest = useCallback(() => {
    if (autoSaveAbortControllerRef.current) {
      autoSaveAbortControllerRef.current.abort();
      autoSaveAbortControllerRef.current = null;
    }
  }, []);

  const clearAutoSaveTimeout = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
  }, []);

  const clearAutoSaveRetryTimeout = useCallback(() => {
    if (autoSaveRetryTimeoutRef.current) {
      clearTimeout(autoSaveRetryTimeoutRef.current);
      autoSaveRetryTimeoutRef.current = null;
    }
  }, []);

  const removeValidatingGroupClientId = useCallback((clientId) => {
    setValidatingGroupClientIds((prev) => prev.filter((id) => id !== clientId));
  }, []);

  const getGroupValidationFingerprint = useCallback((group = {}) => {
    const promptAnswers = (group.promptAnswers || [])
      .filter((answer) => answer && !answer._delete)
      .map(({ field, fieldType, operator, promptId, values }) => ({
        field: field ?? null,
        fieldType: fieldType ?? null,
        operator: operator ?? null,
        promptId: promptId ?? null,
        values: values || [],
      }));

    return JSON.stringify({
      genRepSampleType: group.genRepSampleType ?? null,
      promptAnswers,
      quotaLimit: group.quotaLimit ?? null,
    });
  }, []);

  const cleanupGroupValidation = useCallback(
    (clientId) => {
      const controller = groupValidationAbortControllersRef.current.get(clientId);
      if (controller) {
        controller.abort();
        groupValidationAbortControllersRef.current.delete(clientId);
      }

      pendingGroupValidationClientIdsRef.current.delete(clientId);
      lastValidationFingerprintByClientIdRef.current.delete(clientId);
      removeValidatingGroupClientId(clientId);
    },
    [removeValidatingGroupClientId]
  );

  const queueChangedGroupsForValidation = useCallback(
    (groups = []) => {
      const nextClientIds = new Set(groups.map(({ clientId }) => clientId).filter(Boolean));

      Array.from(lastValidationFingerprintByClientIdRef.current.keys()).forEach((clientId) => {
        if (!nextClientIds.has(clientId)) {
          cleanupGroupValidation(clientId);
        }
      });

      groups.forEach((group) => {
        if (!group?.clientId) return;

        if (group._delete) {
          cleanupGroupValidation(group.clientId);
          return;
        }

        const nextFingerprint = getGroupValidationFingerprint(group);
        const previousFingerprint = lastValidationFingerprintByClientIdRef.current.get(
          group.clientId
        );

        if (previousFingerprint === nextFingerprint) return;

        lastValidationFingerprintByClientIdRef.current.set(group.clientId, nextFingerprint);
        pendingGroupValidationClientIdsRef.current.add(group.clientId);

        const controller = groupValidationAbortControllersRef.current.get(group.clientId);
        if (controller) {
          controller.abort();
          groupValidationAbortControllersRef.current.delete(group.clientId);
        }
        removeValidatingGroupClientId(group.clientId);
      });
    },
    [cleanupGroupValidation, getGroupValidationFingerprint, removeValidatingGroupClientId]
  );

  const mergeGroupValidationFields = useCallback((clientId, fields) => {
    setSurvey((prevSurvey) => {
      const nextGroups = prevSurvey.groups.map((group) =>
        group.clientId === clientId
          ? {
              ...group,
              ...fields,
            }
          : group
      );

      const nextSurvey = {
        ...prevSurvey,
        groups: nextGroups,
      };
      surveyRef.current = nextSurvey;
      return nextSurvey;
    });
  }, []);

  const startRealtimeGroupValidation = useCallback(
    ({ targetVersion }) => {
      if (!isRealtimeSampleValidationEnabled) return;
      if (targetVersion !== surveyVersionRef.current) return;

      const pendingClientIds = Array.from(pendingGroupValidationClientIdsRef.current);
      if (pendingClientIds.length === 0) return;

      pendingClientIds.forEach((clientId) => {
        const group = surveyRef.current?.groups?.find(
          (group) => group.clientId === clientId && !group._delete
        );
        const surveyId = surveyRef.current?.id;

        if (!group || !group.id || !surveyId) return;

        pendingGroupValidationClientIdsRef.current.delete(clientId);

        const controller = new AbortController();
        groupValidationAbortControllersRef.current.set(clientId, controller);

        setValidatingGroupClientIds((prev) =>
          prev.includes(clientId) ? prev : [...prev, clientId]
        );

        validateInstantSurveyGroup({
          groupId: group.id,
          signal: controller.signal,
          surveyId,
        })
          .then((validationFields) => {
            const currentGroup = surveyRef.current?.groups?.find(
              (surveyGroup) =>
                surveyGroup.clientId === clientId &&
                surveyGroup.id === group.id &&
                !surveyGroup._delete
            );
            const isCurrent = currentGroup && targetVersion === surveyVersionRef.current;

            if (!isCurrent) return;

            mergeGroupValidationFields(clientId, validationFields);
          })
          .catch((error) => {
            if (error?.name === 'AbortError' || error?.name === 'CanceledError') return;

            const currentGroup = surveyRef.current?.groups?.find(
              (surveyGroup) =>
                surveyGroup.clientId === clientId &&
                surveyGroup.id === group.id &&
                !surveyGroup._delete
            );
            const isCurrent = currentGroup && targetVersion === surveyVersionRef.current;

            if (!isCurrent) return;

            const validationFields =
              error?.response?.status === 400
                ? {
                    validationDatetime: null,
                    validationStatus: SURVEY_GROUP_VALIDATION_STATUS.NOT_VALIDATED,
                    validationUniqueUsers: null,
                    validationUsers: null,
                  }
                : {
                    validationStatus: SURVEY_GROUP_VALIDATION_STATUS.ERROR,
                  };

            mergeGroupValidationFields(clientId, validationFields);
          })
          .finally(() => {
            const currentController = groupValidationAbortControllersRef.current.get(clientId);
            if (currentController === controller) {
              groupValidationAbortControllersRef.current.delete(clientId);
            }
            removeValidatingGroupClientId(clientId);
          });
      });
    },
    [isRealtimeSampleValidationEnabled, mergeGroupValidationFields, removeValidatingGroupClientId]
  );

  useEffect(() => {
    isComponentMountedRef.current = true;

    return () => {
      // Flag pending async callbacks so they skip setState once unmounted
      isComponentMountedRef.current = false;
      clearAutoSaveTimeout();
      clearAutoSaveRetryTimeout();
      abortAutoSaveRequest();
      groupValidationAbortControllersRef.current.forEach((controller) => controller.abort());
      groupValidationAbortControllersRef.current.clear();
    };
  }, [abortAutoSaveRequest, clearAutoSaveRetryTimeout, clearAutoSaveTimeout]);

  useEffect(() => {
    if (canAutoSave) return;

    clearAutoSaveTimeout();
    clearAutoSaveRetryTimeout();
    abortAutoSaveRequest();
    autoSaveRetryCountRef.current = 0;

    if (isComponentMountedRef.current) {
      setIsAutoSaving(false);
    }
  }, [abortAutoSaveRequest, canAutoSave, clearAutoSaveRetryTimeout, clearAutoSaveTimeout]);
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!isRealtimeSampleValidationEnabled) {
      pendingGroupValidationClientIdsRef.current.clear();
      lastValidationFingerprintByClientIdRef.current.clear();
      groupValidationAbortControllersRef.current.forEach((controller) => controller.abort());
      groupValidationAbortControllersRef.current.clear();
      setValidatingGroupClientIds([]);
      return;
    }

    survey.groups.forEach((group) => {
      if (!group?.clientId || group._delete) return;
      if (lastValidationFingerprintByClientIdRef.current.has(group.clientId)) return;

      lastValidationFingerprintByClientIdRef.current.set(
        group.clientId,
        getGroupValidationFingerprint(group)
      );
    });
  }, [isRealtimeSampleValidationEnabled, survey.groups]);

  useEffect(() => {
    if (!survey.id || !insightsDocument?.id || insightsId) return;

    const nextParams = new URLSearchParams(searchParams);
    const atlasId = String(insightsDocument.id);
    const instantSurveyId = String(survey.id);
    const needsIdUpdate = nextParams.get('id') !== atlasId;
    const needsRefUpdate = nextParams.get('ref') !== instantSurveyId;

    if (!needsIdUpdate && !needsRefUpdate) return;

    nextParams.set('id', atlasId);
    nextParams.set('ref', instantSurveyId);
    isInternalNavigationRef.current = true;
    pendingInternalNavigationParamsRef.current = {
      atlasId,
      instantSurveyId,
    };
    setSearchParams(nextParams, { replace: true });
  }, [insightsDocument?.id, insightsId, searchParams, setSearchParams, survey.id]);

  useEffect(() => {
    if (!isInternalNavigationRef.current) return;

    const pendingParams = pendingInternalNavigationParamsRef.current;
    if (!pendingParams) return;

    const matchesPendingParams =
      searchParams.get('id') === pendingParams.atlasId &&
      searchParams.get('ref') === pendingParams.instantSurveyId;

    if (matchesPendingParams) {
      isInternalNavigationRef.current = false;
      pendingInternalNavigationParamsRef.current = null;
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isStatsigLoading && !isInstantSurveyMaintenance) {
      fetchSurveyData();
      if (isSubscribed) {
        fetchSubscription();
      }
    }
  }, [isInstantSurveyMaintenance, isStatsigLoading, isSubscribed]);

  const fetchSubscription = async () => {
    setSubscriptionError(null);
    setIsLoadingSubscription(true);

    try {
      const subscriptionData = await readClientSubscription();
      const assignedSubscriptionId = clientInfo?.subscriptions?.[0]?.subscriptionId;
      const assignedSubscription = subscriptionData?.find(
        (subscription) => subscription.id === assignedSubscriptionId
      );
      setSubscription(assignedSubscription);
      if (assignedSubscription?.hasStarted) {
        const hasSavedLaunchMethod = Boolean(insightsDocumentRef.current?.paymentType);
        if (hasSavedLaunchMethod) {
          setLaunchMethod(payWithCompletes.key, { markDirty: false });
        }
      }
      return subscriptionData;
    } catch (err) {
      setSubscriptionError(err);
      return null;
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  const fetchSurveyData = async () => {
    try {
      const surveyRequests = [
        readInstantSurveyType({ id: instantSurveyTypeId }),
        readSurveyType({ documentId: instantSurveyTypeId }),
      ];
      const instantSurveyId = searchParams.get('ref');
      const isModal = searchParams.get('modal');
      const hasRefOnly = instantSurveyId && !insightsId;

      if (insightsId && instantSurveyId) {
        surveyRequests.push(
          readInstantSurvey({ id: instantSurveyId }),
          readSurvey({ id: insightsId })
        );
      }
      if ((instantSurveyId && isModal) || hasRefOnly) {
        surveyRequests.push(readInstantSurvey({ id: instantSurveyId }));
      }

      const [
        instantSurveyTypeResponse,
        insightsDocumentTypeResponse,
        instantSurveyDraftResponse,
        insightsDocumentResponse,
      ] = await Promise.all(surveyRequests);

      const insightsDocumentStatus = insightsDocumentResponse?.status;
      if (
        insightsDocumentStatus &&
        !(
          insightsDocumentStatus === surveyStatus.DRAFT ||
          (useCalypsoProvider &&
            insightsDocumentResponse.isPaused &&
            (pausedEditableSurveyStatuses.includes(insightsDocumentStatus) ||
              pausedReadOnlySurveyStatuses.includes(insightsDocumentStatus)))
        )
      ) {
        toast(
          'The survey you are looking for is not available to edit. Please search for it in your Workspace.'
        );
        navigate('/dashboard/workspace/surveys?filters=');
        return;
      }

      setInstantSurveyType(instantSurveyTypeResponse);
      setInsightsDocumentType({
        ...insightsDocumentTypeResponse,
        samplePricing: insightsDocumentTypeResponse.samplePricing.filter(
          ({ isAskWhy, type }) => !isAskWhy && type === 'CREDITS'
        ),
      });

      if (insightsId && instantSurveyId) {
        const { hasQuestionLogicError, ...surveyData } = instantSurveyDraftResponse;
        // if (hasQuestionLogicError) {
        //   toast.error(
        //     'There was an error while rebuilding your questionnaire logic. Please confirm your selections before continuing.'
        //   );
        //   setHadQuestionLogicError(true);
        // }

        syncInsightsDocument(insightsDocumentResponse);
        const savedLaunchMethod = insightsDocumentResponse?.paymentType;

        if (savedLaunchMethod) {
          const launchMethodValue =
            savedLaunchMethod === payWithCompletes.key && isSubscribed
              ? payWithCompletes.key
              : payWithCredits.key;

          setLaunchMethod(launchMethodValue, { markDirty: false });
        }

        lastPersistedTitleRef.current = surveyData.title || '';
        setSurvey((prevSurvey) => {
          const questions =
            surveyData.questions !== undefined
              ? ensureQuestionClientIds(surveyData.questions, prevSurvey.questions)
              : prevSurvey.questions;

          return {
            ...surveyData,
            groups: assignClientIds(surveyData.groups, prevSurvey.groups),
            questions,
          };
        });
      } else if ((instantSurveyId && isModal) || hasRefOnly) {
        const { hasQuestionLogicError, ...surveyData } = instantSurveyDraftResponse;
        lastPersistedTitleRef.current = surveyData.title || '';
        setSurvey((prevSurvey) => {
          const questions =
            surveyData.questions !== undefined
              ? ensureQuestionClientIds(surveyData.questions, prevSurvey.questions)
              : prevSurvey.questions;

          return {
            ...surveyData,
            groups: assignClientIds(surveyData.groups, prevSurvey.groups),
            questions,
            title: surveyData.title === SURVEY_TEMPORARY_TITLE ? '' : surveyData.title,
          };
        });
      }

      // Reset dirty state BEFORE enabling auto-save
      setHasUnsavedChanges(false);
      hasUnsavedChangesRef.current = false;
      surveyVersionRef.current = 0;
      autoSaveRetryCountRef.current = 0;

      clearAutoSaveTimeout();
      clearAutoSaveRetryTimeout();

      setIsLoadingSurvey(false);
    } catch (error) {
      console.error(error);
      setError(error);
    }
  };

  const handleDone = () => {
    isInternalNavigationRef.current = true;
    navigate('/dashboard/workspace/surveys');
  };

  const handleSurveyPublish = ({ subscriptions, userCreditsRemaining }) => {
    LogRocket.track('InstantSurvey_Published', {
      country: survey.country,
      groups: survey.groups.length,
      questions: survey.questions.length,
      ...(survey.surveyTemplateId && { template: survey.surveyTemplateId }),
      type: instantSurveyTypeId,
    });
    dispatch(updateUserCreditsRemaining({ userCreditsRemaining }));

    setHasLaunched(true);

    if (isSubscribed && subscriptions) {
      dispatch(updateUserSubscriptionDetails(subscriptions));

      const updatedSubscription = subscriptions.find(
        (sub) => sub.subscriptionId === subscription?.id
      );
      if (updatedSubscription && subscription?.currentPeriod) {
        setSubscription((prev) => ({
          ...prev,
          currentPeriod: {
            ...prev.currentPeriod,
            remainingCompletes: updatedSubscription.totalRemainingCompletes,
            usedCompletes: updatedSubscription.totalUsedCompletes,
          },
        }));
      }
    }
  };

  const saveInstantSurvey = useCallback(async ({ signal } = {}) => {
    const requestVersion = surveyVersionRef.current;
    const surveySnapshot = surveyRef.current;

    // Snapshot of groups and questions at request-send time so a stale-response merge can
    // bridge backend ids back to local rows via clientId / questionClientId even if the user
    // has renamed or edited them mid-flight. The backend strips both client ids on send and
    // does not echo them back, so the snapshot is the only stable bridge.
    const requestGroupsSnapshot = Array.isArray(surveySnapshot.groups)
      ? surveySnapshot.groups
          .filter((group) => !group?._delete || (group?.id !== null && group?.id !== undefined))
          .map((group) => ({
            clientId: group?.clientId ?? null,
            genRepSampleType: group?.genRepSampleType ?? null,
            id: group?.id ?? null,
            name: group?.name ?? '',
            promptAnswersKey: buildPromptAnswersKey(group?.promptAnswers ?? []),
            quotaLimit: group?.quotaLimit ?? null,
          }))
      : [];
    const requestQuestionsSnapshot = Array.isArray(surveySnapshot.questions)
      ? surveySnapshot.questions.map((question) => ({
          id: question?.id ?? null,
          questionClientId: question?.questionClientId ?? null,
          text: question?.text ?? '',
          type: question?.type ?? '',
        }))
      : [];

    const { id, title, ...surveyData } = surveySnapshot;
    const cleanedTitle = title?.trim() || '';
    const shouldUseAutoTitle = !id && !cleanedTitle;
    const resolvedTitle =
      shouldUseAutoTitle || !cleanedTitle
        ? lastPersistedTitleRef.current || generateTemporarySurveyTitle()
        : cleanedTitle;

    let response;
    if (!id) {
      response = await createInstantSurvey({
        instantSurvey: {
          ...surveyData,
          status: 'DRAFT',
          title: resolvedTitle,
        },
        signal,
      });
    } else {
      response = await updateInstantSurvey({
        instantSurvey: {
          ...surveyData,
          id,
          title: resolvedTitle,
        },
        signal,
      });
    }

    const { title: savedSurveyTitle, ...surveyResponse } = response;

    // Stale-response handling: don't overwrite newer local edits, but still graft the
    // backend-assigned ids onto local rows so the next save targets existing records
    // instead of creating duplicates.
    if (requestVersion < surveyVersionRef.current) {
      lastPersistedTitleRef.current = savedSurveyTitle;
      setSurvey((prevSurvey) => {
        const nextSurvey = mergeServerIdsFromStaleResponse({
          prevSurvey,
          requestSnapshot: {
            groups: requestGroupsSnapshot,
            questions: requestQuestionsSnapshot,
          },
          responseSurvey: surveyResponse,
        });
        surveyRef.current = nextSurvey;
        return nextSurvey;
      });
      return response;
    }

    lastPersistedTitleRef.current = savedSurveyTitle;

    setSurvey((prevSurvey) => {
      const { questions: responseQuestions, ...restSurveyResponse } = surveyResponse;

      // Use local groups as the spine so clientIds stay stable (no remount of in-progress
      // prompt rows) and so unsaved promptAnswers / custom filters aren't dropped when the
      // backend response omits or reorders them. mergeGroups also sweeps locally-deleted
      // rows the backend has already honored so the next save can't 404 on them.
      const nextGroups =
        surveyResponse.groups !== undefined
          ? mergeGroups({
              prevGroups: prevSurvey.groups,
              requestSnapshot: {
                groups: requestGroupsSnapshot,
                questions: requestQuestionsSnapshot,
              },
              responseGroups: surveyResponse.groups,
            })
          : prevSurvey.groups;

      const nextQuestions = mergeQuestions({
        prevQuestions: prevSurvey.questions,
        responseQuestions,
      });

      const nextQuestionLogic =
        surveyResponse.questionLogic !== undefined
          ? mergeQuestionLogic({
              mergedQuestions: nextQuestions,
              prevLogic: prevSurvey.questionLogic,
              responseLogic: surveyResponse.questionLogic,
            })
          : prevSurvey.questionLogic;

      const nextSurvey = {
        ...prevSurvey,
        ...restSurveyResponse,
        groups: nextGroups,
        questionLogic: nextQuestionLogic,
        questions: nextQuestions,
        title: savedSurveyTitle === SURVEY_TEMPORARY_TITLE ? '' : savedSurveyTitle,
      };

      surveyRef.current = nextSurvey;
      return nextSurvey;
    });

    return response;
  }, []);

  const persistSurveyDraft = useCallback(
    async ({ reason = 'auto', signal, targetVersion: initialTargetVersion } = {}) => {
      let targetVersion = initialTargetVersion ?? surveyVersionRef.current;
      const isManualSave = reason !== 'auto';
      clearAutoSaveRetryTimeout();

      if (!isManualSave) {
        const noPendingChanges =
          !hasUnsavedChangesRef.current && targetVersion === surveyVersionRef.current;
        if (!canAutoSaveRef.current || !canAutoSave || isSavingAsDraft || noPendingChanges) {
          return null;
        }
      } else {
        clearAutoSaveTimeout();
      }

      let awaitedPersistResult = null;

      if (persistSurveyDraftPromiseRef.current) {
        if (!isManualSave) {
          return persistSurveyDraftPromiseRef.current.finally(() => {
            if (!isComponentMountedRef.current) {
              return null;
            }

            const hasPendingChanges =
              hasUnsavedChangesRef.current || surveyVersionRef.current !== targetVersion;

            if (!hasPendingChanges) {
              return null;
            }

            return persistSurveyDraft({
              reason: 'auto',
              targetVersion: surveyVersionRef.current,
            });
          });
        }
        const awaitedTargetVersion = targetVersion;
        awaitedPersistResult = await persistSurveyDraftPromiseRef.current;

        const hasPendingChanges =
          hasUnsavedChangesRef.current || surveyVersionRef.current !== awaitedTargetVersion;

        if (!hasPendingChanges) {
          return awaitedPersistResult;
        }
        targetVersion = surveyVersionRef.current;
      }

      const persistPromise = (async () => {
        let requestSignal = signal;

        try {
          if (!isManualSave) {
            if (!canAutoSaveRef.current) {
              return null;
            }
            autoSaveAbortControllerRef.current?.abort();
            autoSaveAbortControllerRef.current = new AbortController();
            requestSignal = autoSaveAbortControllerRef.current.signal;
            if (isComponentMountedRef.current) setIsAutoSaving(true);
          }

          if (isComponentMountedRef.current) setAutoSaveError(null);

          const instantSurveyResponse = await saveInstantSurvey({ signal: requestSignal });
          let atlasSurveyResponse;
          const surveyTitle =
            instantSurveyResponse.title ||
            lastPersistedTitleRef.current ||
            generateTemporarySurveyTitle();
          if (
            !insightsDocumentRef.current?.id ||
            insightsDocumentRef.current?.status === surveyStatus.DRAFT
          ) {
            atlasSurveyResponse = await createAtlasSurvey({
              atlasInstantSurveyRef: instantSurveyResponse.id,
              id: insightsDocumentRef.current?.id,
              paymentMethod: launchMethod,
              status: 'draft',
              title: surveyTitle,
            });
            if (atlasSurveyResponse?.instantSurvey) {
              syncInsightsDocument(atlasSurveyResponse.instantSurvey);
            }
          } else if (insightsDocumentRef.current?.title !== surveyTitle) {
            // INSRV-7023: post-launch advanced edit only renames this document
            await updateSurveyTitle({
              id: insightsDocumentRef.current.id,
              name: surveyTitle,
            });

            syncInsightsDocument({
              ...insightsDocumentRef.current,
              title: surveyTitle,
            });
          }

          const isLatestVersion = targetVersion === surveyVersionRef.current;

          if (isLatestVersion) {
            hasUnsavedChangesRef.current = false;
            autoSaveRetryCountRef.current = 0;

            if (isComponentMountedRef.current) {
              setHasUnsavedChanges(false);
              if (!isManualSave) setLastAutoSavedAt(new Date());
            }

            startRealtimeGroupValidation({ targetVersion });
          }

          return { atlasSurveyResponse, instantSurveyResponse };
        } catch (error) {
          // Silently ignore aborted requests
          if (error?.name === 'AbortError' || error?.name === 'CanceledError') {
            return null;
          }

          if (isManualSave) {
            throw error;
          }

          if (autoSaveRetryCountRef.current < MAX_AUTO_SAVE_RETRIES) {
            autoSaveRetryCountRef.current += 1;

            clearAutoSaveRetryTimeout();
            autoSaveRetryTimeoutRef.current = setTimeout(
              () => {
                autoSaveRetryTimeoutRef.current = null;
                if (isComponentMountedRef.current && canAutoSaveRef.current) {
                  persistSurveyDraft({
                    reason: 'auto',
                    targetVersion,
                  });
                }
              },
              1000 * Math.pow(2, autoSaveRetryCountRef.current)
            );

            return null;
          }

          autoSaveRetryCountRef.current = 0;
          if (isComponentMountedRef.current) setAutoSaveError(error);
          console.error('Auto-save error:', error);
          return null;
        } finally {
          if (!isManualSave && isComponentMountedRef.current) {
            setIsAutoSaving(false);
            if (autoSaveAbortControllerRef.current?.signal === requestSignal) {
              autoSaveAbortControllerRef.current = null;
            }
          }
          if (persistSurveyDraftPromiseRef.current === persistPromise) {
            persistSurveyDraftPromiseRef.current = null;
          }
        }
      })();

      persistSurveyDraftPromiseRef.current = persistPromise;
      return persistPromise;
    },
    [
      canAutoSave,
      clearAutoSaveRetryTimeout,
      clearAutoSaveTimeout,
      isSavingAsDraft,
      launchMethod,
      saveInstantSurvey,
      startRealtimeGroupValidation,
      syncInsightsDocument,
    ]
  );

  const scheduleAutoSave = useCallback(
    ({ targetVersion = surveyVersionRef.current } = {}) => {
      if (!canAutoSaveRef.current) return;

      clearAutoSaveTimeout();

      autoSaveTimeoutRef.current = setTimeout(() => {
        autoSaveTimeoutRef.current = null;
        if (!canAutoSaveRef.current) return;

        const hasChanges =
          hasUnsavedChangesRef.current || targetVersion !== surveyVersionRef.current;

        if (!isComponentMountedRef.current || !hasChanges) return;

        persistSurveyDraft({ reason: 'auto', targetVersion });
      }, AUTO_SAVE_DEBOUNCE_MS);
    },
    [clearAutoSaveTimeout, persistSurveyDraft]
  );

  const setLaunchMethod = useCallback(
    (nextLaunchMethod, { markDirty = true } = {}) => {
      const didChange = launchMethod !== nextLaunchMethod;

      if (didChange) {
        setLaunchMethodState(nextLaunchMethod);
      }

      if (!markDirty || !didChange) return;

      surveyVersionRef.current += 1;
      const targetVersion = surveyVersionRef.current;

      setHasUnsavedChanges(true);
      hasUnsavedChangesRef.current = true;

      scheduleAutoSave({ targetVersion });
    },
    [launchMethod, scheduleAutoSave]
  );

  const updateSurvey = useCallback(
    (fields, { markDirty = false } = {}) => {
      let nextQuestionLogicIsComplete = true;
      let isQuestionLogicUpdate = false;

      setSurvey((prev) => {
        const nextFields = { ...fields };

        if (fields.questions !== undefined) {
          nextFields.questions = ensureQuestionClientIds(fields.questions, prev.questions);
        }

        const next = { ...prev, ...nextFields };
        surveyRef.current = next;

        if (fields.questionLogic !== undefined) {
          isQuestionLogicUpdate = true;
          nextQuestionLogicIsComplete = isQuestionLogicComplete(next.questionLogic);
        }

        return next;
      });

      if (!markDirty) return;

      if (isRealtimeSampleValidationEnabled && fields.groups !== undefined) {
        queueChangedGroupsForValidation(fields.groups);
      }

      surveyVersionRef.current += 1;
      const targetVersion = surveyVersionRef.current;

      setHasUnsavedChanges(true);
      hasUnsavedChangesRef.current = true;

      // don't auto-save while logic is mid-build (prevents server overwrite)
      if (isQuestionLogicUpdate && !nextQuestionLogicIsComplete) {
        clearAutoSaveTimeout(); // cancel any pending save that would wipe in-progress logic
        return;
      }

      scheduleAutoSave({ targetVersion });
    },
    [
      clearAutoSaveTimeout,
      isRealtimeSampleValidationEnabled,
      queueChangedGroupsForValidation,
      scheduleAutoSave,
    ]
  );

  const handleBlockedNavigation = useCallback(async () => {
    try {
      await persistSurveyDraft({ reason: 'navigation', targetVersion: surveyVersionRef.current });
    } catch (err) {
      console.error('Failed to save before navigation:', err);
    } finally {
      blocker.proceed();
    }
  }, [blocker, persistSurveyDraft]);

  if (isInstantSurveyMaintenance) {
    return <MaintenanceView heading={MAINTENANCE_MESSAGE} logo={<NumeratorLogo />} />;
  }
  if (error) {
    return error?.code === 'ERR_NETWORK' ? (
      <SurveyNetworkError />
    ) : (
      <Error status={error?.response?.status} />
    );
  }

  if (hasLaunched) return <Confirmation />;
  if (isLoadingSurvey || isStatsigLoading) {
    return (
      <LoaderSkeleton height={450}>
        <rect height="40" rx="4" ry="4" width="1333" x="0" y="0" />
        <rect height="130" rx="4" ry="4" width="1333" x="0" y="60" />
        <rect height="40" rx="4" ry="4" width="1333" x="0" y="210" />
        <rect height="20" rx="4" ry="4" width="1333" x="0" y="270" />
        <rect height="20" rx="2" ry="2" width="175" x="0" y="310" />
        <rect height="15" rx="2" ry="2" width="200" x="0" y="350" />
        <rect height="15" rx="2" ry="2" width="175" x="0" y="375" />
        <rect height="15" rx="2" ry="2" width="225" x="0" y="400" />
        <rect height="15" rx="2" ry="2" width="200" x="0" y="425" />
        <rect height="15" rx="2" ry="2" width="225" x="0" y="450" />
      </LoaderSkeleton>
    );
  }

  return (
    <SurveyContext.Provider
      value={{
        activeGroups,
        activeQuestions,
        activeStep,
        autoSaveError,
        currentQuestionCount,
        currentQuestionnairePrice,
        hasUnsavedChanges,
        insightsDocument,
        insightsDocumentType,
        instantSurveyType,
        isAutoSaving,
        isLoadingSubscription,
        isReadOnly,
        isRealtimeSampleValidationEnabled,
        isResearchObjectivesTooLong:
          survey.researchObjectives?.length > RESEARCH_OBJECTIVES_MAX_LENGTH,
        isSaveAsDraftOpen,
        isSavingAsDraft,
        isSubscribed,
        jobId,
        lastAutoSavedAt,
        launchMethod,
        maxQuestions: isVideoSurvey ? 1 : insightsDocumentType.maxQuestions,
        persistSurveyDraft,
        questionDisplayIndexById,
        saveInstantSurvey,
        setActiveStep,
        setIsSaveAsDraftOpen,
        setIsSavingAsDraft,
        setJobId,
        setLaunchMethod,
        setShowAIGenerationModal,
        setShowSurveyTemplateModal,
        setSurveyGoals,
        showAIGenerationModal,
        showSurveyTemplateModal,
        subscription,
        subscriptionError,
        survey,
        surveyGoals,
        updateSurvey,
        validatingGroupClientIds,
      }}
    >
      {isReadOnly && (
        <Announcement
          header="Read-Only Survey"
          icon={<LockIcon />}
          text="This survey is currently read-only. Please contact support if you need assistance."
          variant="info"
        />
      )}
      {pausedReadOnlySurveyStatuses.includes(insightsDocument?.status) && canAdvancedEditSurvey && (
        <Announcement
          header="Editing a Fielded Survey"
          icon={<ExclamationTriangleIcon />}
          text="This survey has already started receiving responses. Changes here may impact data collection."
          variant="warn"
        />
      )}
      {activeStep === SURVEY_STEPS.PANELISTS && (
        <Panelists
          defaultResponseCount={defaultResponseCount}
          onNext={() => setActiveStep(SURVEY_STEPS.QUESTIONNAIRE)}
        />
      )}
      {activeStep === SURVEY_STEPS.QUESTIONNAIRE && (
        <Questionnaire
          onNext={() => setActiveStep(SURVEY_STEPS.REVIEW)}
          onPrevious={() => setActiveStep(SURVEY_STEPS.PANELISTS)}
        />
      )}
      {activeStep === SURVEY_STEPS.REVIEW && (
        <Review onDone={handleDone} onPublish={handleSurveyPublish} setActiveStep={setActiveStep} />
      )}
      {blocker?.state === 'blocked' && (
        <ModalUnsavedChanges onClose={blocker.reset} onContinue={handleBlockedNavigation} />
      )}
      {infoModalConfig && (
        <Modal
          buttons={[<Button key="close" onClick={closeInfoModal} text="Close" variant="primary" />]}
          className={styles['create-instant-survey-info-modal']}
          isActive={isInfoModalOpen}
          onClose={closeInfoModal}
          title={infoModalConfig.title}
        >
          <p>
            {infoModalConfig.description}{' '}
            <a href={infoModalConfig.link} rel="noreferrer" target="_blank">
              Learn more
            </a>
          </p>
          <h3>
            <ShieldTipIcon />
            Setup
          </h3>
          <ol>
            {infoModalConfig.steps.map((step) => (
              <li key={step}>
                <CheckSquareIcon />
                {step}
              </li>
            ))}
          </ol>
        </Modal>
      )}
    </SurveyContext.Provider>
  );
};

const CreateInstantSurveyLayout = () => (
  <Page>
    <Layout.Column>
      <CreateInstantSurvey />
    </Layout.Column>
  </Page>
);

export default CreateInstantSurveyLayout;
